import { useState } from "react";
import { api, gbp, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { confirmAction } from "@/components/ConfirmHost";

const today = () => new Date().toISOString().slice(0, 10);

// Payment history for one PO: list of part-payments plus an add-payment row.
export default function POPayments({ poId, total, payments = [], onChange }) {
  const paidSoFar = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const left = Math.max(0, (Number(total) || 0) - paidSoFar);
  const [date, setDate] = useState(today());
  const [amount, setAmount] = useState(left ? left.toFixed(2) : "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!(Number(amount) > 0)) return toast.error("Enter an amount above £0");
    setBusy(true);
    try {
      const r = await api.post(`/purchase-orders/${poId}/payments`, { date, amount: Number(amount), note });
      toast.success(`Payment of ${gbp(Number(amount))} logged`);
      const rem = Math.max(0, (Number(total) || 0) - r.data.amount_paid);
      setAmount(rem ? rem.toFixed(2) : ""); setNote("");
      onChange?.(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!(await confirmAction(`Remove the ${gbp(p.amount)} payment on ${fmtDate(p.date)}?`))) return;
    try { const r = await api.delete(`/purchase-orders/${poId}/payments/${p.id}`); onChange?.(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="po-payments">
      <div className="grid grid-cols-3 gap-3 text-sm">
        <Stat label="PO total" value={gbp(total)} />
        <Stat label="Paid so far" value={gbp(paidSoFar)} testid="po-payments-paid" />
        <Stat label="Left to pay" value={gbp(left)} testid="po-payments-left" tone={left > 0.005 ? "text-amber-700" : "text-emerald-700"} />
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
          <th className="py-2">Date</th><th className="py-2 text-right">Amount</th><th className="py-2 pl-4">Note</th><th className="py-2">Logged by</th><th></th>
        </tr></thead>
        <tbody>
          {payments.length === 0 && <tr><td colSpan="5" className="py-4 text-center text-slate-500">No payments logged yet.</td></tr>}
          {payments.map((p) => (
            <tr key={p.id} className="border-b border-slate-100" data-testid={`po-payment-${p.id}`}>
              <td className="py-2 whitespace-nowrap">{fmtDate(p.date)}</td>
              <td className="py-2 text-right tabular-nums font-medium">{gbp(p.amount)}</td>
              <td className="py-2 pl-4 text-slate-600">{p.note || "—"}</td>
              <td className="py-2 text-slate-500 text-xs">{p.created_by || "—"}</td>
              <td className="py-2 text-right">
                <button type="button" onClick={() => remove(p)} className="text-red-600 hover:text-red-700" data-testid={`po-payment-del-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap items-end gap-2 border-t border-slate-200 pt-4">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-40 rounded-sm" data-testid="po-payment-date" />
        <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount £" className="h-9 w-32 rounded-sm tabular-nums" data-testid="po-payment-amount" />
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional, e.g. bank ref)" className="h-9 flex-1 min-w-[10rem] rounded-sm" data-testid="po-payment-note" />
        <Button type="button" onClick={add} disabled={busy} className="h-9 rounded-sm bg-slate-900 hover:bg-slate-800 text-white" data-testid="po-payment-add">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Add payment</>}
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "text-slate-900", testid }) {
  return (
    <div className="border border-slate-200 rounded-sm px-3 py-2">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-semibold tabular-nums ${tone}`} data-testid={testid}>{value}</div>
    </div>
  );
}
